import { useParams } from 'react-router-dom';

import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import type { ShortBreadcrumb } from '@restore/breadcrumbs/breadcrumbs.types';

import { useBreadcrumbs } from './useBreadcrumbs';

export type TabBreadcrumbMap = Record<string, ShortBreadcrumb>;

/**
 * Sets the breadcrumb trail for a clinical-trial sub-section that has tabs:
 * `Clinical Trials / <trial name> / <active tab>`.
 *
 * The active-tab crumb is looked up in `tabBreadcrumbs` by the active URL
 * segment, falling back to `fallbackSegment` when the segment is unknown (or
 * absent). The trial name and `:id` route param are read internally so callers
 * only supply the tab config.
 */
export const useClinicalTrialTabBreadcrumbs = (
  tabBreadcrumbs: TabBreadcrumbMap,
  activeSegment: string | undefined,
  fallbackSegment: string,
  options?: { enabled?: boolean }
) => {
  const { id } = useParams();
  const clinicalTrial = useClinicalTrialSlice(state => state.data);

  const activeTabCrumb =
    tabBreadcrumbs[activeSegment ?? ''] ?? tabBreadcrumbs[fallbackSegment];

  // Guard against an unknown segment AND a fallback missing from the map: an
  // undefined crumb would leave a hole that crashes createBreadcrumbs when it
  // destructures `[title, pathKey]`. Drop it instead and keep the parent trail.
  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [clinicalTrial?.name, 'clinicalTrialGeneralInformation'],
      ...(activeTabCrumb ? [activeTabCrumb] : []),
    ],
    [id],
    options
  );
};
